import { isBefore, parseISO, set, startOfDay } from 'date-fns';
import * as React from 'react';
import { Alert, Button, Card, Dropdown, DropdownButton, Form, Modal, Spinner } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { useBeforeUnload, useBlocker, useFetcher, useLoaderData, useLocation } from 'react-router-dom';

import { Expenses } from '@/components/expenses';
import { FormButtons } from '@/components/formbuttons';
import { InfoTable } from '@/components/infotable';
import { InputNumber } from '@/components/input-number';
import { InputTextArea } from '@/components/input-textarea';
import { ListLink } from '@/components/listlink';
import { ModalForm } from '@/components/modal-form';
import { Patcher } from '@/components/patcher';
import { PatcherSwitch } from '@/components/patcher-switch';
import { SelectorAPIGroup } from '@/components/selector-api-group';
import { SelectorAPIHelper } from '@/components/selector-api-helper';
import { SelectorDate } from '@/components/selector-date';
import { SelectorList } from '@/components/selector-list';
import { SelectorListMultiple } from '@/components/selector-list-multiple';
import { Services } from '@/components/services';
import { Stock } from '@/components/stock';
import { deleteSingle, getSingle, patchSingle, postSingle, putSingle } from '@/helper/apicalls';
import { WorkOrder } from '@/helper/interfaces';
import { backupDrives, frameRates } from '@/helper/lists';
import { formatDate, formatErrors, formatMoney, formatTime } from '@/helper/utility';
import { useStore } from '@/store';

import { baseURL } from './const';

export async function singleLoader({ params }: any) {
  if (Number.isInteger(Number.parseInt(params.id, 10))) {
    return getSingle(baseURL, params.id);
  } else {
    throw new TypeError('404! This is not a valid URL.');
  }
}

export async function singleAction({ request, params }: any) {
  const body = Object.fromEntries(await request.formData());
  const redirectTo = body.redirectTo;
  delete body.redirectTo;

  if (body.companyId) body.companyId = JSON.parse(body.companyId);
  if (body.contactId) body.contactId = JSON.parse(body.contactId);
  if (body.projectId) body.projectId = JSON.parse(body.projectId);

  if (request.method == 'POST') {
    return postSingle(baseURL, body);
  } else if (request.method == 'PUT') {
    return putSingle(baseURL, params.id, body);
  } else if (request.method == 'PATCH') {
    return patchSingle(baseURL, params.id, body);
  } else if (request.method == 'DELETE') {
    return deleteSingle(baseURL, params.id, redirectTo);
  } else {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }
}

export async function infoAction({ request, params }: any) {
  const body = Object.fromEntries(await request.formData());

  if (body.frameRates) body.frameRates = JSON.parse(body.frameRates as string);

  // eslint-disable-next-line unicorn/prefer-ternary
  if (request.method == 'PUT') {
    return putSingle(baseURL + '/info', params.id, body);
  } else {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function Single() {
  const { user } = useStore();
  const data = useLoaderData() as WorkOrder;

  const location = useLocation();
  const [redirectTo] = React.useState(location.state?.from || baseURL);

  const normalizeNotes = React.useCallback((value: string) => value.trim(), []);

  const notesFetcher = useFetcher();
  const [notes, setNotes] = React.useState(data.notes ?? '');
  const [notesSuccess, setNotesSuccess] = React.useState('');
  const [notesErrors, setNotesErrors] = React.useState<Record<string, string>>({});
  const isSavingNotes = notesFetcher.state === 'loading' || notesFetcher.state === 'submitting';
  const savedNotes = data.notes ?? '';
  const notesIsDirty = normalizeNotes(notes) !== normalizeNotes(savedNotes);

  const notesBlocker = useBlocker(notesIsDirty);

  useBeforeUnload(
    React.useCallback(
      (event: BeforeUnloadEvent) => {
        if (notesIsDirty) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [notesIsDirty]
    )
  );

  React.useEffect(() => {
    if (notesFetcher.data?.status === 200) {
      setNotesErrors({});
      setNotesSuccess('Saved');
    } else if (Array.isArray(notesFetcher.data)) {
      setNotesErrors(formatErrors(notesFetcher.data));
      setNotesSuccess('');
    } else if (typeof notesFetcher.data === 'string') {
      setNotesErrors({ notes: notesFetcher.data });
      setNotesSuccess('');
    }
  }, [notesFetcher.data]);

  React.useEffect(() => {
    if (!notesIsDirty && notesFetcher.state === 'idle') {
      setNotes(savedNotes);
    }
  }, [savedNotes, notesFetcher.state]);

  const assistants = data.session?.assistants ?? [];
  const orderedAssistants = React.useMemo(() => {
    const assistantIds = data.session?.assistantIds ?? [];
    if (assistantIds.length === 0) return assistants;
    const indexMap = new Map(assistantIds.map((id, index) => [id, index]));
    return [...assistants].sort((a, b) => (indexMap.get(a.id) ?? 0) - (indexMap.get(b.id) ?? 0));
  }, [assistants, data.session?.assistantIds]);

  const disabled =
    data?.deleted || data?.job?.deleted || (data?.invoiceId ? true : false) || !user?.role?.editWorkOrders;
  const disabledManager =
    !user?.role?.manageWorkOrders &&
    data.isFinished &&
    isBefore(startOfDay(parseISO(data.date)), startOfDay(new Date()));

  const unusedTimeUnits = data.unusedTime > 1 ? ' hrs' : ' hr';
  const scheduleStartTime = data.session?.scheduleStartTime ?? data.session?.startTime;
  const scheduleEndTime = data.session?.scheduleEndTime ?? data.session?.endTime;

  const modal = React.useRef(null);
  const modalInfo = React.useRef(null);
  const modalPrice = React.useRef(null);
  const groupHelper = React.useRef(null);
  const [elementSelect, setElementSelect] = React.useState<Partial<WorkOrder>>();

  function editWorkOrder() {
    setElementSelect({});
    modal.current.openModal();
  }

  function saveForm() {
    setElementSelect({
      companyId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="companyId"]').value, 10),
      contactId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="contactId"]').value, 10),
      projectId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="projectId"]').value, 10),
      date: document.querySelector<HTMLInputElement>('input[name="date"]').value
    });
  }

  function saveNotes(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const notesNormalized = normalizeNotes(notes);
    setNotes(notesNormalized);
    notesFetcher.submit(
      { notes: notesNormalized },
      {
        method: 'put',
        action: baseURL + '/notes/' + data.id
      }
    );
  }

  return (
    <React.Fragment>
      {!user?.role?.editWorkOrders && (
        <Alert variant='warning' className='mt-3'>
          You are not authorized to edit this page.
        </Alert>
      )}
      {data.deleted && user?.role?.editWorkOrders && (
        <Alert variant='danger' className='mt-3'>
          This work order was deleted and can no longer be edited.
        </Alert>
      )}
      {data?.job?.deleted && !data.deleted && user?.role?.editWorkOrders && (
        <Alert variant='danger' className='mt-3'>
          The parent&nbsp;
          <ListLink to={'/jobs/' + data.job.id} nowrapper>
            Job {data.job.id}
          </ListLink>
          &nbsp;was deleted so this work order can no longer be edited.
        </Alert>
      )}
      {data?.invoiceId && user?.role?.editWorkOrders && (
        <Alert variant='warning' className='mt-3'>
          This work order was added to&nbsp;
          <ListLink to={'/invoices/' + data.invoiceId} nowrapper>
            Inv {data.invoiceId}
          </ListLink>
          &nbsp;and can not longer be edited
        </Alert>
      )}
      {data.isFinished && !data.invoiceId && user?.role?.editWorkOrders && (
        <Alert variant='warning' className='mt-3'>
          Work orders that have been finished can no longer be edited.
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Card.Title className='clearfix'>
            Work Order #{data.id}
            {!data?.session && user?.role?.manageWorkOrders ? (
              <DropdownButton
                title={
                  <React.Fragment>
                    <PencilSquare className='icon-align' /> Edit
                  </React.Fragment>
                }
                size='sm'
                className='float-end'
                disabled={disabled}
              >
                <Dropdown.Item onClick={editWorkOrder}>Work Order</Dropdown.Item>
                <Dropdown.Item onClick={() => modalInfo.current.openModal()}>Titles and Technical Data</Dropdown.Item>
              </DropdownButton>
            ) : (
              <Button
                type='button'
                size='sm'
                className='float-end'
                onClick={() => modalInfo.current.openModal()}
                disabled={disabled}
              >
                <PencilSquare className='icon-align' /> Titles and Technical Data
              </Button>
            )}
          </Card.Title>
          <InfoTable
            elements={[
              { title: 'Company', content: data.company.name, link: '/companies/' + data.company.id },
              { title: 'Contact', content: data.contact.name, link: '/contacts/' + data.contact.id },
              { title: 'Project', content: data.project.name, link: '/projects/' + data.project.id },
              ...(data.session
                ? [
                    {
                      title: 'Mixer',
                      content: data.session.mixer.name,
                      link: user?.role?.users ? '/users/' + data.session.mixer.id : undefined
                    },
                    ...(orderedAssistants.length > 0
                      ? [
                          {
                            title: orderedAssistants.length === 1 ? 'Assistant' : 'Assistants',
                            content: user?.role?.users
                              ? orderedAssistants.map((assistant, index) => (
                                  <React.Fragment key={assistant.id}>
                                    {index > 0 && ', '}
                                    <ListLink to={'/users/' + assistant.id} nowrapper>
                                      {assistant.name}
                                    </ListLink>
                                  </React.Fragment>
                                ))
                              : orderedAssistants.map((assistant) => assistant.name).join(', ')
                          }
                        ]
                      : []),
                    {
                      title: 'Session',
                      content:
                        formatDate(scheduleStartTime) +
                        ' ' +
                        formatTime(scheduleStartTime) +
                        ' - ' +
                        formatTime(scheduleEndTime) +
                        ' in Studio ' +
                        data.session?.room?.name
                    }
                  ]
                : [{ title: 'Date', content: formatDate(data?.date) }]),
              ...(data.job
                ? [
                    {
                      title: 'Job',
                      content: 'Job ' + data.job?.id.toString(),
                      link: '/jobs/' + data.job?.id
                    }
                  ]
                : []),
              {
                title: 'Notes',
                subContent: [
                  { title: 'Company', content: data.company.notes },
                  { title: 'Contact', content: data.contact.notes },
                  { title: 'Project', content: data.project.notes },
                  ...(user?.role?.privateNotes && data.project.privateNotes?.length > 0
                    ? [{ title: 'Producer', content: data.project.privateNotes }]
                    : []),
                  ...(data.session ? [{ title: 'Session', content: data.session.notes }] : [])
                ]
              }
            ]}
          />

          <Form noValidate className='mt-3' onSubmit={saveNotes}>
            <Form.Group controlId='notes'>
              <Form.Label>Assistant Notes</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                name='notes'
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                  setNotesSuccess('');
                }}
                disabled={disabled || isSavingNotes}
                isInvalid={Boolean(notesErrors.notes)}
              />
              <Form.Control.Feedback type='invalid'>{notesErrors.notes}</Form.Control.Feedback>
            </Form.Group>
            <div className='d-flex align-items-center justify-content-end gap-2 mt-2'>
              {notesIsDirty ? (
                <span className='text-danger small'>Unsaved Changes</span>
              ) : (
                notesSuccess && <span className='text-success small'>{notesSuccess}</span>
              )}
              {isSavingNotes && <Spinner animation='border' size='sm' />}
              {user?.role?.editWorkOrders && (
                <Button type='submit' size='sm' disabled={disabled || isSavingNotes || !notesIsDirty}>
                  Save Notes
                </Button>
              )}
            </div>
          </Form>

          <hr />
          <Patcher
            id={data.id}
            values={{ isFinished: data.isFinished.toString() }}
            url='/workorders'
            disabled={disabled || disabledManager}
          >
            <PatcherSwitch
              name='isFinished'
              displayName='Session Finished'
              tooltip={!disabled && disabledManager ? 'Cannot edit past work orders' : ''}
            />
          </Patcher>
        </Card.Body>
      </Card>

      <Services
        baseURL={baseURL}
        id={data.id}
        services={data.services}
        total={data.servicesTotal}
        mixer={data?.session?.mixer}
        initStartTime={
          data?.session?.startTime || set(new Date(data.date), { hours: 10, minutes: 0, seconds: 0 }).toISOString()
        }
        disabled={disabled || data.isFinished}
      />

      <Stock
        baseURL={baseURL}
        id={data.id}
        stock={data.stock}
        total={data.stockTotal}
        disabled={disabled || data.isFinished}
      />

      <Expenses
        baseURL={baseURL}
        id={data.id}
        expenses={data.expenses}
        total={data.expensesTotal}
        disabled={disabled || data.isFinished}
      />

      {user?.role?.managePrices && (
        <Card className='mt-3'>
          <Card.Body>
            <Card.Title>
              Totals
              <Button
                type='button'
                size='sm'
                className='float-end'
                onClick={() => modalPrice.current.openModal()}
                disabled={disabled || data.isFinished}
              >
                <PencilSquare className='icon-align' /> Edit
              </Button>
            </Card.Title>
            {data.session?.noCharge && <span className='ms-3'>** No Charge Session **</span>}
            <InfoTable
              elements={[
                { title: 'Audio Services', content: formatMoney(data.servicesTotal) },
                ...(data.unusedTime
                  ? [
                      {
                        title: 'Unused Time',
                        content:
                          data.unusedTime +
                          unusedTimeUnits +
                          ' x ' +
                          formatMoney(data.unusedRate) +
                          '/hr = ' +
                          formatMoney(data.unusedTimeTotal)
                      }
                    ]
                  : []),
                { title: 'Stock', content: formatMoney(data.stockTotal) },
                { title: 'Expenses', content: formatMoney(data.expensesTotal) },
                ...(data.priceAdjustment
                  ? [{ title: 'Price Adjustment', content: formatMoney(data.priceAdjustment) }]
                  : []),
                ...(data.adjustmentNote?.length > 0 ? [{ title: 'Notes', content: data.adjustmentNote }] : []),
                { title: 'Work Order Total', content: formatMoney(data.workOrderTotal) }
              ]}
            />
          </Card.Body>
        </Card>
      )}

      <FormButtons
        type='Work Order'
        redirectTo={redirectTo}
        showDelete={!disabled && !data.isFinished && user?.role?.manageWorkOrders}
      >
        <React.Fragment>Are you sure you want to delete this work order?</React.Fragment>
      </FormButtons>

      <Modal
        show={notesBlocker.state === 'blocked'}
        onHide={() => notesBlocker.reset?.()}
        backdrop='static'
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>You have unsaved changes to Notes. Are you sure you want to leave this page?</Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' type='button' onClick={() => notesBlocker.reset?.()}>
            Stay
          </Button>
          <Button variant='danger' type='button' onClick={() => notesBlocker.proceed?.()}>
            Leave
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalForm
        title='Edit Titles and Technical Data'
        size='xl'
        dialogClassName='workorder-info-modal'
        url={baseURL + '/info'}
        ref={modalInfo}
        putId={data.id}
        draggable
      >
        <SelectorList
          name='backup'
          displayName='Unified Backup'
          list={backupDrives}
          size='col-12 col-md-4'
          value={data?.backup}
        />
        <SelectorListMultiple
          name='frameRates'
          displayName='Frame Rate(s)'
          list={frameRates}
          size='col-12 col-md-8'
          value={data?.frameRates || []}
        />
        <InputTextArea name='voTalent' displayName='VO Talent' size='col-12' rows={4} value={data?.voTalent || ''} />
        <div className='w-100'>
          <hr className='my-3' />
        </div>
        <InputTextArea
          name='titles'
          displayName='Spot Titles'
          size='col-12 col-lg-6'
          rows={18}
          value={data?.titles || ''}
        />
        <InputTextArea
          name='filename'
          displayName='Pro Tools Session Name(s)'
          size='col-12 col-lg-6'
          rows={18}
          value={data?.filename || ''}
        />
      </ModalForm>

      <ModalForm title='Edit Work Order' url={baseURL} putId={data.id} ref={modal} draggable>
        <SelectorDate value={data?.date} valueSelect={elementSelect?.date} />
        <SelectorAPIGroup
          value={{ companyId: data?.companyId, contactId: data?.contactId, projectId: data?.projectId }}
          valueSelect={{
            companyId: elementSelect?.companyId,
            contactId: elementSelect?.contactId,
            projectId: elementSelect?.projectId
          }}
          addCompany={() => groupHelper.current.addCompany()}
          addContact={() => groupHelper.current.addContact()}
          linkContact={() => groupHelper.current.linkContact()}
          addProject={() => groupHelper.current.addProject()}
        />
      </ModalForm>

      <SelectorAPIHelper
        elementSelect={elementSelect}
        setElementSelect={setElementSelect}
        saveForm={saveForm}
        modal={modal}
        ref={groupHelper}
      />

      <ModalForm title='Edit Price' url={baseURL + '/prices'} ref={modalPrice} putId={data.id}>
        {data.unusedTime > 0 && (
          <InputNumber name='customUnusedRate' value={data.customUnusedRate?.toString()} money perHour />
        )}
        <InputNumber
          name='priceAdjustment'
          value={data.priceAdjustment?.toString()}
          negative
          money
          inactive={data.session?.noCharge}
        />
        <InputTextArea name='adjustmentNote' value={data.adjustmentNote} />
      </ModalForm>
    </React.Fragment>
  );
}
